import { useEffect, useState } from 'react';
import {
  customerPaymentsApi,
  apiFetch,
  type BankDetails,
  type PaymentProof,
  type PaymentProvider,
} from '../lib/api';
import {
  Landmark,
  Copy,
  Check,
  Upload,
  Link2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

/**
 * SPEI (and other out-of-band transfer) payment for an invoice.
 *
 * The money moves through the customer's bank, not through us, so this is a
 * two-step flow: show where to send it, then collect evidence that they did.
 * Staff review the evidence and approve, which is what actually settles the
 * invoice. Nothing here marks anything paid.
 */
interface Props {
  invoiceId: string;
  invoiceNumber: string;
  amountDue: number;
  currency: string;
  onSubmitted: () => void;
}

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];

const statusStyles: Record<PaymentProof['status'], { icon: typeof Clock; bg: string; color: string; label: string }> = {
  PENDING: { icon: Clock, bg: 'bg-yellow-100', color: 'text-yellow-700', label: 'Awaiting review' },
  APPROVED: { icon: CheckCircle, bg: 'bg-green-100', color: 'text-green-700', label: 'Approved' },
  REJECTED: { icon: XCircle, bg: 'bg-red-100', color: 'text-red-700', label: 'Rejected' },
};

function CopyableField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is unavailable over plain HTTP and in some browsers; the
      // value is on screen either way, so this is not worth surfacing.
    }
  }

  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <div className="flex items-center gap-2">
        <p className="font-medium font-mono break-all">{value}</p>
        <button
          type="button"
          onClick={copy}
          className="text-gray-400 hover:text-gray-700 shrink-0"
          aria-label={`Copy ${label}`}
        >
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

export default function BankTransferPanel({
  invoiceId,
  invoiceNumber,
  amountDue,
  currency,
  onSubmitted,
}: Props) {
  const { t } = useTranslation();
  const [provider, setProvider] = useState<PaymentProvider | null>(null);
  const [existingProof, setExistingProof] = useState<PaymentProof | null>(null);
  const [wire, setWire] = useState<{
    instructions?: { reference?: string; notes?: string };
    summary?: {
      total: number;
      approved: number;
      remaining: number;
      canSubmit: boolean;
      proofs: Array<{ id: string; amount: number; approvedAmount: number | null; status: string; createdAt: string; reviewNotes: string | null }>;
    };
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const [mode, setMode] = useState<'FILE' | 'URL'>('FILE');
  const [file, setFile] = useState<File | null>(null);
  const [proofUrl, setProofUrl] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, [invoiceId]);

  async function load() {
    try {
      setLoading(true);
      const [providersRes, proofsRes, wireRes] = await Promise.all([
        customerPaymentsApi.getProviders(),
        customerPaymentsApi.listProofs(),
        // Instructions and the running total come from the API rather than
        // being assembled here: the reference the admin reconciles by is a
        // business setting, not something the browser should invent.
        apiFetch<any>(`/customer/payments/invoices/${invoiceId}/wire`).catch(() => null),
      ]);

      const transfer = (providersRes.data || []).find((p) => p.supportsProofUpload) || null;
      setProvider(transfer);
      setWire((wireRes as any)?.data ?? null);

      const forInvoice = (proofsRes.data || []).filter((p) => p.invoiceId === invoiceId);
      setExistingProof(forInvoice[0] || null);
    } catch {
      setProvider(null);
    } finally {
      setLoading(false);
    }
  }

  function formatCurrency(amount: number) {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(amount / 100);
  }

  function pickFile(selected: File | null) {
    setError(null);
    if (!selected) {
      setFile(null);
      return;
    }
    // Checked here as well as server-side so the customer finds out before
    // uploading rather than after.
    if (!ACCEPTED_TYPES.includes(selected.type)) {
      setError('Attach a PNG, JPG, WEBP or PDF file.');
      return;
    }
    if (selected.size > MAX_FILE_BYTES) {
      setError('That file is larger than 10 MB.');
      return;
    }
    setFile(selected);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (mode === 'FILE' && !file) {
      setError('Attach your transfer receipt, or switch to pasting a link.');
      return;
    }
    if (mode === 'URL' && !proofUrl.trim()) {
      setError('Paste a link to your transfer receipt.');
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'FILE' && file) {
        const form = new FormData();
        form.append('file', file);
        form.append('amount', String(amountDue));
        form.append('currency', currency);
        if (reference.trim()) form.append('reference', reference.trim());
        if (notes.trim()) form.append('notes', notes.trim());
        await customerPaymentsApi.submitInvoiceProof(invoiceId, form);
      } else {
        await customerPaymentsApi.submitInvoiceProof(invoiceId, {
          // What is still owed after earlier transfers, not the original
          // total — otherwise a second transfer claims the whole invoice again.
          amount: wire?.summary?.remaining ?? amountDue,
          currency,
          proofUrl: proofUrl.trim(),
          reference: reference.trim() || undefined,
          notes: notes.trim() || undefined,
        });
      }

      setFile(null);
      setProofUrl('');
      setReference('');
      setNotes('');
      await load();
      onSubmitted();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 flex items-center gap-2 text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('transfer.loading')}
      </div>
    );
  }

  // Nothing to show when the provider has not enabled bank transfer.
  if (!provider) return null;

  const bank: BankDetails = provider.bankDetails || {};
  const hasBankDetails = Boolean(bank.clabe || bank.accountNumber || bank.iban);

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-6">
      <div className="flex items-center gap-2">
        <Landmark className="h-5 w-5 text-brand-600" />
        <h2 className="text-lg font-semibold text-gray-900">{provider.displayName}</h2>
      </div>

      {existingProof && (
        <div
          className={clsx(
            'rounded-lg p-4 flex items-start gap-3',
            statusStyles[existingProof.status].bg
          )}
        >
          {(() => {
            const Icon = statusStyles[existingProof.status].icon;
            return <Icon className={clsx('h-5 w-5 mt-0.5', statusStyles[existingProof.status].color)} />;
          })()}
          <div className="text-sm">
            <p className={clsx('font-medium', statusStyles[existingProof.status].color)}>
              {statusStyles[existingProof.status].label}
            </p>
            <p className="text-gray-700 mt-1">
              {formatCurrency(existingProof.amount)} submitted on{' '}
              {new Date(existingProof.createdAt).toLocaleDateString()}
              {existingProof.reference ? ` · ref ${existingProof.reference}` : ''}
            </p>
            {existingProof.status === 'PENDING' && (
              <p className="text-gray-600 mt-1">
                {t('transfer.staysActive')}
              </p>
            )}
            {existingProof.status === 'REJECTED' && existingProof.reviewNotes && (
              <p className="text-gray-700 mt-1">Reason: {existingProof.reviewNotes}</p>
            )}
          </div>
        </div>
      )}

      {wire?.summary && wire.summary.proofs.length > 0 && (
        <div className="border border-gray-200 rounded-lg divide-y text-sm">
          {wire.summary.proofs.map((p) => (
            <div key={p.id} className="px-3 py-2 flex items-center justify-between gap-3">
              <span className="text-gray-600">
                {new Date(p.createdAt).toLocaleDateString()} · {formatCurrency(p.amount)}
                {p.approvedAmount != null && p.approvedAmount !== p.amount && (
                  <span className="text-amber-700"> · accepted {formatCurrency(p.approvedAmount)}</span>
                )}
                {p.reviewNotes && <span className="block text-xs text-gray-500">{p.reviewNotes}</span>}
              </span>
              <span
                className={clsx(
                  'text-xs font-medium px-2 py-0.5 rounded-full',
                  p.status === 'APPROVED' && 'bg-green-50 text-green-700',
                  p.status === 'PENDING' && 'bg-amber-50 text-amber-700',
                  p.status === 'REJECTED' && 'bg-red-50 text-red-700'
                )}
              >
                {p.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {hasBankDetails ? (
        <div>
          <p className="text-sm text-gray-600 mb-3">
            Transfer {formatCurrency(wire?.summary?.remaining ?? amountDue)} to the account below, then send
            us the receipt.
          </p>

          {/* A transfer can land short, so what is still owed is the number
              that matters — not the invoice total. */}
          {wire?.summary && wire.summary.approved > 0 && wire.summary.remaining > 0 && (
            <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              We have received {formatCurrency(wire.summary.approved)} of{' '}
              {formatCurrency(wire.summary.total)}. Outstanding:{' '}
              <strong>{formatCurrency(wire.summary.remaining)}</strong>. Send another transfer for the
              difference and upload the receipt below.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
            {bank.accountName && (
              <div>
                <p className="text-sm text-gray-500">{t('transfer.accountName')}</p>
                <p className="font-medium">{bank.accountName}</p>
              </div>
            )}
            {bank.bankName && (
              <div>
                <p className="text-sm text-gray-500">{t('transfer.bank')}</p>
                <p className="font-medium">{bank.bankName}</p>
              </div>
            )}
            {bank.clabe && <CopyableField label="CLABE" value={bank.clabe} />}
            {bank.accountNumber && <CopyableField label="Account number" value={bank.accountNumber} />}
            {bank.iban && <CopyableField label="IBAN" value={bank.iban} />}
            {bank.swift && <CopyableField label="SWIFT" value={bank.swift} />}
            <CopyableField label="Reference" value={wire?.instructions?.reference || invoiceNumber} />
            <div>
              <p className="text-sm text-gray-500">{t('transfer.amount')}</p>
              <p className="font-medium">{formatCurrency(amountDue)}</p>
            </div>
          </div>
          {bank.referenceFormat && (
            <p className="text-xs text-gray-500 mt-2">{bank.referenceFormat}</p>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <AlertCircle className="h-4 w-4" />
          {t('transfer.noDetails')}
        </div>
      )}

      {existingProof?.status === 'PENDING' ? (
        <p className="text-sm text-gray-500">
          We already have a receipt for this invoice under review. Contact support if you need to
          replace it.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setMode('FILE'); setError(null); }}
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
                mode === 'FILE' ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700'
              )}
            >
              <Upload className="h-4 w-4" />
              {t('transfer.uploadReceipt')}
            </button>
            <button
              type="button"
              onClick={() => { setMode('URL'); setError(null); }}
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
                mode === 'URL' ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700'
              )}
            >
              <Link2 className="h-4 w-4" />
              {t('transfer.pasteLink')}
            </button>
          </div>

          {mode === 'FILE' ? (
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('transfer.receiptHint')}</label>
              <input
                type="file"
                accept={ACCEPTED_TYPES.join(',')}
                onChange={(e) => pickFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('transfer.linkToReceipt')}</label>
              <input
                type="url"
                value={proofUrl}
                onChange={(e) => setProofUrl(e.target.value)}
                placeholder="https://..."
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('transfer.reference')}</label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={t('transfer.trackingKey')}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">{t('transfer.notes')}</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Send receipt
          </button>
          <p className="text-xs text-gray-500">
            Your invoice is marked paid once we confirm the transfer, usually within one business day.
          </p>
        </form>
      )}
    </div>
  );
}
